import type {
  PresetOptionButton,
  PresetOptionColor,
  PresetOptionSpacing,
  TextPack,
  BorderPack,
  CornerPack,
  SpacingPack,
  Preset,
  PresetOption,
  ButtonPack,
  ColorPack,
  PresetOptionCorner,
  PresetOptionType,
} from '@modules/global-style';
import { COLOR_KEYS_MAP } from '@modules/global-style';

export const mutations = {
  SET_STEP_PRESET: 'stepPreset',
  COMMIT_PRESETS: 'commitPreset',
  SET_LOADING_PRESETS: 'setLoadingPreset',
  COMMIT_PAGES: 'commitPages',
  SET_CURRENT_PRESET: 'setCurrentPreset',
  SET_CURRENT_PRESET_IN_PAGE: 'setCurrentPresetInPage',
  SET_PRESET_TYPE_ID: 'setPresetTypeId',
  PRESET_MODAL: 'presetModal',
  PRESET_MODAL_NOTIFY: 'presetModalNotify',
  RELOAD_PAGE_FONTS: 'RELOAD_PAGE_FONTS',
  SET_PRESET_MODAL_NOTIFY: 'SET_PRESET_MODAL_NOTIFY',
  SET_CURRENT_PRESET_OPTIONS: 'setCurrentPresetOptions',
  SET_PERSET_TO_PRESET_LIST: 'setPresetToPresetList',
  SET_PREVIEW_PRESET: 'setPreviewPreset',
  SET_LINKED_PRESET_ID: 'setLinkedPresetId',
  SET_OLD_LINKED_PRESET_ID: 'SET_OLD_LINKED_PRESET_ID',
  CHANGE_ACTIVE_TAB: 'CHANGE_ACTIVE_TAB',
  SET_VIEW_TARGET_PRESET: 'SET_VIEW_TARGET_PRESET',
  SET_COMPONENT_TARGET_GLOBAL_STYLE: 'SET_COMPONENT_TARGET_GLOBAL_STYLE',
  SET_STYLE_ID_TARGET_GLOBAL_STYLE: 'SET_STYLE_ID_TARGET_GLOBAL_STYLE',
  SET_IS_CALLING_API: 'SET_IS_CALLING_API',
  SET_IS_OPEN_SETTING: 'SET_IS_OPEN_SETTING',
  SET_IS_PUBLISH_PRESET: 'SET_IS_PUBLISH_PRESET',
  SET_APPLIED_GS_FOR_PAGE: 'SET_APPLIED_GS_FOR_PAGE',
  SET_SIDE_BAR_SETTING_ELEMENT_ID: 'SET_SIDE_BAR_SETTING_ELEMENT_ID',
  SET_SNIPPERS: 'SET_SNIPPERS',
  SET_LOADING_SNIPPETS: 'SET_LOADING_SNIPPETS',
  ADD_NEW_SNIPPETS: 'ADD_NEW_SNIPPETS',
  DELETE_SNIPPET: 'DELETE_SNIPPET',
  UPDATE_ELEMENT_SETTING_ACTIVE: 'UPDATE_ELEMENT_SETTING_ACTIVE',
};

export const getters = {
  GET_LINKED_PRESET_ID: 'getLinkedPresetId',
  GET_OLD_LINKED_PRESET_ID: 'getOldLinkedPresetId',
  GET_CURRENT_PRESET: 'getCurrentPreset',
  GET_CURRENT_PRESET_IN_PAGE: 'getCurrentPresetInPage',
  GET_CURRENT_PRESET_BORDERS: 'getCurrentPresetBorders',
  GET_CURRENT_PRESET_SPACINGS: 'getCurrentPresetSpacings',
  GET_CURRENT_PRESET_CORNERS: 'getCurrentPresetCorners',
  GET_CURRENT_PRESET_TEXTS: 'getCurrentPresetTexts',
  GET_CURRENT_PRESET_BUTTONS: 'getCurrentPresetButtons',
  GET_CURRENT_PRESET_COLOR: 'getCurrentPresetColor',
  GET_CURRENT_PRESET_OPTIONS: 'getCurrentPresetOptions',
  GET_PREVIEW_PRESET: 'getPreviewPreset',
  GET_PRESET_BY_PAGE_PRESET_ID: 'getPresetByPagePresetId',
  GET_STATUS_PAGE_SAVE_GS: 'getStatusPageSaveGs',
  GET_IS_CALLING_API: 'getIsCallingApi',
  GET_APPLIED_GS_FOR_PAGE: 'GET_APPLIED_GS_FOR_PAGE',
  GET_IS_OPEN_SETTING: 'GET_IS_OPEN_SETTING',
  PRESET_IS_EDITED: 'PRESET_IS_EDITED',
};

export const actions = {
  FETCH_PRESETS: 'fetchPresets',
  FETCH_PAGES: 'fetchPages',
  SAVE_PRESET_OPTIONS: 'savePresetOptions',
  UPDATE_PRESET: 'updatePreset',
  DUPLICATE_PRESET: 'duplicatePreset',
  DELETE_PRESET: 'deletePreset',
  CRAWL_FROM_LIVE_THEME: 'crawlFromLiveTheme',
  CREATE_PRESET: 'createPreset',
  PUBLIC_PRESET: 'publicPreset',
  APPLY_PRESET: 'applyPreset',
  UPDATE_LINKED_PRESET_LIST: 'updateLinkedPresetList',

  // NEW
  FETCH_SNIPPETS: 'FETCH_SNIPPETS',
};

export type ProcessStepPreset = {
  type: PresetOptionType;
  target: PresetOption;
};

export const DEFAULT_COLOR_NEUTRAL = {
  color1: '#f7f7f7',
  color2: '#aaaaaa',
  color3: '#1a1a1a',
};
export const DEFAULT_COLOR_BRAND = {
  color1: '#7795ff',
  color2: '#3c67ff',
  color3: '#2d4dbf',
};
export const DEFAULT_COLOR_ACCENT = {
  color1: '#ffe5d5',
  color2: '#ffeaa2',
  color3: '#d4ff09',
  color4: '',
  color5: '',
  color6: '',
  color7: '#00000000',
};
export const DEFAULT_COLOR_PACKS: ColorPack[] = [
  {
    brand: DEFAULT_COLOR_BRAND,
    neutral: DEFAULT_COLOR_NEUTRAL,
    accent: DEFAULT_COLOR_ACCENT,
  },
];

export const DEFAULT_BORDER_PACKS: BorderPack[] = [
  {
    style: 'solid',
    thickness: '1px',
    dash: '4px',
    gap: '4px',
    dashCap: 'none',
    color: 'var(--gf_gs-color3-neutral)',
  },
  {
    style: 'dashed',
    thickness: '1px',
    dash: '4px',
    gap: '4px',
    dashCap: 'none',
    color: 'var(--gf_gs-color3-neutral)',
  },
  {
    style: 'dotted',
    thickness: '1px',
    dash: '4px',
    gap: '4px',
    dashCap: 'none',
    color: 'var(--gf_gs-color3-neutral)',
  },
];

export const DEFAULT_CORNER_PACKS: CornerPack[] = [
  {
    mode: 'all',
    value: '0px 0px 0px 0px',
  },
];

export const DEFAULT_SPACING_PACKS: SpacingPack[] = [
  {
    value: '8px',
  },
  {
    value: '16px',
  },
  {
    value: '32px',
  },
  {
    value: '56px',
  },
  {
    value: '72px',
  },
];

export const DEFAULT_BUTTON_PACKS: ButtonPack[] = [
  {
    buttonStyle: 'text',
    textStyle: {
      large: 'paragraph-1',
      medium: 'paragraph-2',
      small: 'paragraph-3',
    },
    borderStyle: null,
    cornerStyle: null,
    dropShadow: {
      enabled: false,
      angle: '0deg',
      distance: '4px',
      blur: '30px',
      size: '0px',
      color: '',
    },
    textColor: {
      origin: 'var(--gf_gs-color1-neutral)',
      hover: 'var(--gf_gs-color1-neutral)',
    },
    backgroundColor: {
      origin: 'var(--gf_gs-color2-brand)',
      hover: 'var(--gf_gs-color1-brand)',
    },
  },
  {
    buttonStyle: 'text',
    textStyle: {
      large: 'paragraph-1',
      medium: 'paragraph-2',
      small: 'paragraph-3',
    },
    borderStyle: null,
    cornerStyle: null,
    dropShadow: {
      enabled: false,
      angle: '0deg',
      distance: '4px',
      blur: '30px',
      size: '0px',
      color: '',
    },
    textColor: {
      origin: 'var(--gf_gs-color1-neutral)',
      hover: 'var(--gf_gs-color1-neutral)',
    },
    backgroundColor: {
      origin: 'var(--gf_gs-color2-brand)',
      hover: 'var(--gf_gs-color1-brand)',
    },
  },
  {
    buttonStyle: 'text',
    textStyle: {
      large: 'paragraph-1',
      medium: 'paragraph-2',
      small: 'paragraph-3',
    },
    borderStyle: null,
    cornerStyle: null,
    dropShadow: {
      enabled: false,
      angle: '0deg',
      distance: '4px',
      blur: '30px',
      size: '0px',
      color: '',
    },
    textColor: {
      origin: 'var(--gf_gs-color1-neutral)',
      hover: 'var(--gf_gs-color1-neutral)',
    },
    backgroundColor: {
      origin: 'var(--gf_gs-color2-brand)',
      hover: 'var(--gf_gs-color1-brand)',
    },
  },
  {
    buttonStyle: 'text',
    textStyle: {
      large: 'paragraph-1',
      medium: 'paragraph-2',
      small: 'paragraph-3',
    },
    borderStyle: null,
    cornerStyle: null,
    dropShadow: {
      enabled: false,
      angle: '0deg',
      distance: '4px',
      blur: '30px',
      size: '0px',
      color: '',
    },
    textColor: {
      origin: 'var(--gf_gs-color1-neutral)',
      hover: 'var(--gf_gs-color1-neutral)',
    },
    backgroundColor: {
      origin: 'var(--gf_gs-color2-brand)',
      hover: 'var(--gf_gs-color1-brand)',
    },
  },
  {
    buttonStyle: 'text',
    textStyle: {
      large: 'paragraph-1',
      medium: 'paragraph-2',
      small: 'paragraph-3',
    },
    borderStyle: null,
    cornerStyle: null,
    dropShadow: {
      enabled: false,
      angle: '0deg',
      distance: '4px',
      blur: '30px',
      size: '0px',
      color: '',
    },
    textColor: {
      origin: 'var(--gf_gs-color1-neutral)',
      hover: 'var(--gf_gs-color1-neutral)',
    },
    backgroundColor: {
      origin: 'var(--gf_gs-color2-brand)',
      hover: 'var(--gf_gs-color1-brand)',
    },
  },
];

export const DEFAULT_TEXT_PACKS: TextPack[] = [
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '2.37rem',
    decoration: 'none',
    lineHeight: '1.2em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '2.37rem',
      },
      md: {
        fontSize: '2.37rem',
      },
      sm: {
        fontSize: '1.90rem',
      },
      xs: {
        fontSize: '1.90rem',
      },
    },
  },
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '1.78rem',
    decoration: 'none',
    lineHeight: '1.2em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '1.78rem',
      },
      md: {
        fontSize: '1.78rem',
      },
      sm: {
        fontSize: '1.42rem',
      },
      xs: {
        fontSize: '1.42rem',
      },
    },
  },
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '1.333rem',
    decoration: 'none',
    lineHeight: '1.2em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '1.333rem',
      },
      md: {
        fontSize: '1.333rem',
      },
      sm: {
        fontSize: '1.07rem',
      },
      xs: {
        fontSize: '1.07rem',
      },
    },
  },
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '1rem',
    decoration: 'none',
    lineHeight: '1.5em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '1rem',
      },
      md: {
        fontSize: '1rem',
      },
      sm: {
        fontSize: '0.8rem',
      },
      xs: {
        fontSize: '0.8rem',
      },
    },
  },
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '0.75rem',
    decoration: 'none',
    lineHeight: '1.5em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '0.75rem',
      },
      md: {
        fontSize: '0.75rem',
      },
      sm: {
        fontSize: '0.6rem',
      },
      xs: {
        fontSize: '0.6rem',
      },
    },
  },
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 700,
    fontStyle: 'normal',
    fontSize: '0.56rem',
    decoration: 'none',
    lineHeight: '1.5em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '0.56rem',
      },
      md: {
        fontSize: '0.56rem',
      },
      sm: {
        fontSize: '0.45rem',
      },
      xs: {
        fontSize: '0.45rem',
      },
    },
  },
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '1rem',
    decoration: 'none',
    lineHeight: '1.5em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '1rem',
      },
      md: {
        fontSize: '1rem',
      },
      sm: {
        fontSize: '0.8rem',
      },
      xs: {
        fontSize: '0.8rem',
      },
    },
  },
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '0.75rem',
    decoration: 'none',
    lineHeight: '1.5em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '0.75rem',
      },
      md: {
        fontSize: '0.75rem',
      },
      sm: {
        fontSize: '0.6rem',
      },
      xs: {
        fontSize: '0.6rem',
      },
    },
  },
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '0.56rem',
    decoration: 'none',
    lineHeight: '1.5em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '0.56rem',
      },
      md: {
        fontSize: '0.56rem',
      },
      sm: {
        fontSize: '0.45rem',
      },
      xs: {
        fontSize: '0.45rem',
      },
    },
  },
  {
    baseSize: '16px',
    scale: '1.333',
    headingFont: 'inherit',
    paragraphFont: 'inherit',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '0.42rem',
    decoration: 'none',
    lineHeight: '1.5em',
    letterSpacing: '0px',
    fontColor: `var(--${COLOR_KEYS_MAP['neutral-color3']})`,
    screens: {
      lg: {
        fontSize: '0.42rem',
      },
      md: {
        fontSize: '0.42rem',
      },
      sm: {
        fontSize: '0.34rem',
      },
      xs: {
        fontSize: '0.34rem',
      },
    },
  },
];

export const DEFAULT_PRESET: Preset = {
  id: 1,
  name: 'GemPages Default Preset',
  destroy: false,
  isEdited: false,
  presetOptions: {
    texts: [
      {
        id: 1,
        name: 'Heading 1',
        isDefault: true,
        type: 'text',
        subType: 'heading',
        key: 'heading-1',
        value: DEFAULT_TEXT_PACKS[0],
      },
      {
        id: 2,
        name: 'Heading 2',
        isDefault: true,
        type: 'text',
        subType: 'heading',
        key: 'heading-2',
        value: DEFAULT_TEXT_PACKS[1],
      },
      {
        id: 3,
        name: 'Heading 3',
        isDefault: true,
        type: 'text',
        subType: 'heading',
        key: 'heading-3',
        value: DEFAULT_TEXT_PACKS[2],
      },
      {
        id: 4,
        name: 'Heading 4',
        isDefault: true,
        type: 'text',
        subType: 'heading',
        key: 'heading-4',
        value: DEFAULT_TEXT_PACKS[3],
      },
      {
        id: 5,
        name: 'Heading 5',
        isDefault: true,
        type: 'text',
        subType: 'heading',
        key: 'heading-5',
        value: DEFAULT_TEXT_PACKS[4],
      },
      {
        id: 6,
        name: 'Heading 6',
        isDefault: true,
        type: 'text',
        subType: 'heading',
        key: 'heading-6',
        value: DEFAULT_TEXT_PACKS[5],
      },
      {
        id: 7,
        name: 'Paragraph 1',
        isDefault: true,
        type: 'text',
        subType: 'paragraph',
        key: 'paragraph-1',
        value: DEFAULT_TEXT_PACKS[6],
      },
      {
        id: 8,
        name: 'Paragraph 2',
        isDefault: true,
        type: 'text',
        subType: 'paragraph',
        key: 'paragraph-2',
        value: DEFAULT_TEXT_PACKS[7],
      },
      {
        id: 9,
        name: 'Paragraph 3',
        isDefault: true,
        type: 'text',
        subType: 'paragraph',
        key: 'paragraph-3',
        value: DEFAULT_TEXT_PACKS[8],
      },
      {
        id: 10,
        name: 'Paragraph 4',
        isDefault: true,
        type: 'text',
        subType: 'paragraph',
        key: 'paragraph-4',
        value: DEFAULT_TEXT_PACKS[9],
      },
    ],
    colors: [
      {
        id: 11,
        name: 'Color',
        isDefault: true,
        type: 'color',
        key: 'color',
        value: DEFAULT_COLOR_PACKS[0],
      },
    ],
    borders: [
      {
        id: 13,
        name: 'Border Solid',
        isDefault: true,
        type: 'border',
        key: 'border-solid',
        value: DEFAULT_BORDER_PACKS[0],
      },
      {
        id: 14,
        name: 'Border Dashed',
        isDefault: true,
        type: 'border',
        key: 'border-dashed',
        value: DEFAULT_BORDER_PACKS[1],
      },
      {
        id: 15,
        name: 'Border Dotted',
        isDefault: true,
        type: 'border',
        key: 'border-dotted',
        value: DEFAULT_BORDER_PACKS[2],
      },
    ],
    corners: [
      {
        id: 16,
        name: 'Default Corner',
        isDefault: true,
        type: 'corner',
        key: 'corner',
        value: DEFAULT_CORNER_PACKS[0],
      },
    ],
    spacings: [
      {
        id: 17,
        name: 'XS',
        isDefault: true,
        type: 'spacing',
        key: 'xs',
        value: DEFAULT_SPACING_PACKS[0],
      },
      {
        id: 18,
        name: 'S',
        isDefault: true,
        type: 'spacing',
        key: 'sm',
        value: DEFAULT_SPACING_PACKS[1],
      },
      {
        id: 19,
        name: 'M',
        isDefault: true,
        type: 'spacing',
        key: 'md',
        value: DEFAULT_SPACING_PACKS[2],
      },
      {
        id: 20,
        name: 'L',
        isDefault: true,
        type: 'spacing',
        key: 'lg',
        value: DEFAULT_SPACING_PACKS[3],
      },
      {
        id: 21,
        name: 'XL',
        isDefault: true,
        type: 'spacing',
        key: 'xl',
        value: DEFAULT_SPACING_PACKS[4],
      },
    ],
    buttons: [
      {
        id: 22,
        name: 'Button',
        isDefault: true,
        type: 'button',
        key: 'element',
        value: DEFAULT_BUTTON_PACKS[0],
      },
      {
        id: 23,
        name: '(P) Cart Button',
        isDefault: true,
        type: 'button',
        key: 'cart-button',
        value: DEFAULT_BUTTON_PACKS[1],
      },
      {
        id: 24,
        name: '(P) View More',
        isDefault: true,
        type: 'button',
        key: 'view-more',
        value: DEFAULT_BUTTON_PACKS[2],
      },
      {
        id: 25,
        name: 'Submit Button',
        isDefault: true,
        type: 'button',
        key: 'submit',
        value: DEFAULT_BUTTON_PACKS[3],
      },
      {
        id: 26,
        name: '(P) Dynamic Checkout',
        isDefault: true,
        type: 'button',
        key: 'check-button',
        value: DEFAULT_BUTTON_PACKS[4],
      },
    ],
  },
};

export const DEFAULT_PRESET_OPTION: PresetOption = {
  id: -1,
  name: 'N/A',
  isDefault: true,
  type: 'text',
  subType: 'heading',
  key: 'N/A',
  value: {
    value: 'N/A',
  },
};
export const DEFAULT_SPACING_PRESET_OPTION: PresetOptionSpacing = {
  id: -1,
  name: 'N/A',
  isDefault: true,
  type: 'spacing',
  key: 'N/A',
  value: {
    value: 'N/A',
  },
};
export const DEFAULT_SPACING_PRESETS: PresetOptionSpacing[] = [DEFAULT_SPACING_PRESET_OPTION];

export const DEFAULT_CONNER_PRESET_OPTION: PresetOptionCorner = {
  id: -1,
  name: 'N/A',
  isDefault: true,
  type: 'corner',
  key: 'N/A',
  value: DEFAULT_CORNER_PACKS[0],
};
export const DEFAULT_CONNER_PRESET: PresetOptionCorner[] = [DEFAULT_CONNER_PRESET_OPTION];

export const DEFAULT_PRESET_CLASS = {
  texts: {
    'heading-1': {
      title: 'Heading 1',
      class: 'gf_gs-text-heading-1',
    },
    'heading-2': {
      title: 'Heading 2',
      class: 'gf_gs-text-heading-2',
    },
    'heading-3': {
      title: 'Heading 3',
      class: 'gf_gs-text-heading-3',
    },
    'heading-4': {
      title: 'Heading 4',
      class: 'gf_gs-text-heading-4',
    },
    'heading-5': {
      title: 'Heading 5',
      class: 'gf_gs-text-heading-5',
    },
    'heading-6': {
      title: 'Heading 6',
      class: 'gf_gs-text-heading-6',
    },
    'paragraph-1': {
      title: 'Paragraph 1',
      class: 'gf_gs-text-paragraph-1',
    },
    'paragraph-2': {
      title: 'Paragraph 2',
      class: 'gf_gs-text-paragraph-2',
    },
    'paragraph-3': {
      title: 'Paragraph 3',
      class: 'gf_gs-text-paragraph-3',
    },
    'paragraph-4': {
      title: 'Paragraph 4',
      class: 'gf_gs-text-paragraph-4',
    },
  },
  colors: {
    brand: {
      color1: {
        title: 'Color 7',
        class: 'gf_gs-color1-brand',
      },
      color2: {
        title: 'Brand Color',
        class: 'gf_gs-color2-brand',
      },
      color3: {
        title: 'Color 8',
        class: 'gf_gs-color3-brand',
      },
    },
    neutral: {
      color1: {
        title: 'Color 1',
        class: 'gf_gs-color1-neutral',
      },
      color2: {
        title: 'Color 2',
        class: 'gf_gs-color2-neutral',
      },
      color3: {
        title: 'Color 3',
        class: 'gf_gs-color3-neutral',
      },
    },
    accent: {
      color1: {
        title: 'Color 4',
        class: 'gf_gs-color1-accent',
      },
      color2: {
        title: 'Color 5',
        class: 'gf_gs-color2-accent',
      },
      color3: {
        title: 'Color 6',
        class: 'gf_gs-color3-accent',
      },
      color4: {
        title: 'Color 10',
        class: 'gf_gs-color4-accent',
      },
      color5: {
        title: 'Color 11',
        class: 'gf_gs-color5-accent',
      },
      color6: {
        title: 'Color 12',
        class: 'gf_gs-color6-accent',
      },
      color7: {
        title: 'Color 9',
        class: 'gf_gs-color7-accent',
      },
    },
    default: {
      black: {
        title: 'Black',
        class: 'gf_gs-black-default',
      },
      white: {
        title: 'White',
        class: 'gf_gs-white-default',
      },
      transparent: {
        title: 'Transparent',
        class: 'gf_gs-transparent-default',
      },
    },
  },
  borders: {
    'border-solid': {
      title: 'Border Solid',
      class: 'gf_gs-border-solid',
    },
    'border-dashed': {
      title: 'Border Dash',
      class: 'gf_gs-border-dash',
    },
    'border-dotted': {
      title: 'Border Dot',
      class: 'gf_gs-border-dot',
    },
  },
  corners: {
    corner: {
      title: 'Default Corner',
      class: 'gf_gs-corner-corner',
    },
  },
  buttons: {},
};
export const DEFAULT_BUTTON_PRESET_OPTION: PresetOptionButton = {
  id: -1,
  name: 'N/A',
  isDefault: true,
  type: 'button',
  key: 'N/A',
  value: DEFAULT_BUTTON_PACKS[0],
};
export const DEFAULT_BUTTON_PRESET: PresetOptionButton[] = [DEFAULT_BUTTON_PRESET_OPTION];

export const DEFAULT_COLOR_PRESET_OPTION: PresetOptionColor = {
  id: -1,
  name: 'N/A',
  isDefault: true,
  type: 'color',
  key: 'N/A',
  value: DEFAULT_COLOR_PACKS[0],
};
export const DEFAULT_COLOR_PRESET: PresetOptionColor[] = [DEFAULT_COLOR_PRESET_OPTION];

export const DEFAULT_COLOR: any = {
  // "default-black": "#000000FF",
  // "default-white": "#FFFFFFFF",
  // "default-transparent": "",
};

export const BUTTON_TYPE_LARGE = 'button-large';
export const BUTTON_TYPE_MEDIUM = 'button-medium';
export const BUTTON_TYPE_SMALL = 'button-small';

export const ICON_TEXT_BUTTON = 'icon_text';
export const ICON_BUTTON = 'icon';
export const TEXT_BUTTON = 'text';
export const BEFORE_ICON_POSITION = 'before';
export const AFTER_ICON_POSITION = 'after';

export const DISPLAY_TITLE_BUTTON: any = {
  element: 'Button',
  'cart-button': 'Add To Cart',
  'view-more': 'View More',
  'check-button': 'Checkout',
  submit: 'Submit Button',
};

// check button can setting icon or not
export const IS_SETTING_ICON_IN_BUTTON = false;

export const CLASS_GS_CUSTOM = 'gf_gs_ct';
export const SPACING_DATA_KEY = 'gs_gf-key';
